import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import DialogHeader from "../components/DialogHeader"
import {
  INTAKE_BRAND_OPTIONS,
  brandOptionLabel,
} from "../settings/brokerageProfileIntakeChoices"
import BoostCampaignMediaUpload from "./BoostCampaignMediaUpload"
import {
  EMPTY_BOOST_CAMPAIGN_CRITERIA,
  TARGET_TYPE_OPTIONS,
  fetchBoostCampaignCriteriaFull,
  numericInputProps,
  saveBoostCampaignCriteriaToApi,
  validateBoostCampaignCriteria,
} from "./boostCampaignCriteria"

function brandCodesToOptions(codes) {
  return (Array.isArray(codes) ? codes : [])
    .map((code) => {
      const option = INTAKE_BRAND_OPTIONS.find((entry) => entry.value === code)
      return (
        option || {
          value: code,
          label: brandOptionLabel(code),
        }
      )
    })
    .filter((option) => option.value)
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(criteria: object) => void | Promise<void>} props.onApply After Submit (Airtable sync + apply filters).
 * @param {() => void} [props.onSaved] After Save (database only).
 */
export default function BoostLeadsWithCampaignDialog({
  open,
  onClose,
  onApply,
  onSaved,
}) {
  const axios = useAuthorizedAxiosClient()
  const [form, setForm] = useState({ ...EMPTY_BOOST_CAMPAIGN_CRITERIA })
  const [fieldErrors, setFieldErrors] = useState({})
  const [loadError, setLoadError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isMediaBusy, setIsMediaBusy] = useState(false)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    if (!open) {
      return
    }
    setFieldErrors({})
    setLoadError(null)
    setSaveError(null)
    setIsLoading(true)
    fetchBoostCampaignCriteriaFull(axios)
      .then(({ criteria }) => setForm(criteria))
      .catch(() => {
        setLoadError("Could not load saved campaign criteria.")
      })
      .finally(() => setIsLoading(false))
  }, [open, axios])

  function handleSoftClose() {
    onClose()
  }

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }))
    setSaveError(null)
    setFieldErrors((prev) => {
      if (!prev[name]) {
        return prev
      }
      const next = { ...prev }
      delete next[name]
      return next
    })
  }

  async function handleSaveClicked() {
    const errors = validateBoostCampaignCriteria(form)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setIsSaving(true)
    setSaveError(null)
    try {
      const saved = await saveBoostCampaignCriteriaToApi(axios, form, {
        syncToAirtable: false,
      })
      setForm(saved.criteria)
      if (onSaved) {
        onSaved(saved)
      }
      onClose()
    } catch (err) {
      const apiFieldErrors = err.response?.data?.field_errors
      if (apiFieldErrors && typeof apiFieldErrors === "object") {
        setFieldErrors(apiFieldErrors)
      } else {
        setSaveError(
          err.response?.data?.detail || "Could not save campaign criteria."
        )
      }
    } finally {
      setIsSaving(false)
    }
  }

  async function handleSubmitClicked() {
    const errors = validateBoostCampaignCriteria(form)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    setIsSubmitting(true)
    setSaveError(null)
    try {
      const saved = await saveBoostCampaignCriteriaToApi(axios, form, {
        syncToAirtable: true,
      })
      setForm(saved.criteria)
      await onApply(saved)
      onClose()
    } catch (err) {
      const apiFieldErrors = err.response?.data?.field_errors
      if (apiFieldErrors && typeof apiFieldErrors === "object") {
        setFieldErrors(apiFieldErrors)
      } else {
        setSaveError(
          err.response?.data?.detail ||
            err.response?.data?.airtable_sync_error ||
            "Could not submit campaign criteria."
        )
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const persistInProgress = isSaving || isSubmitting || isMediaBusy

  return (
    <Dialog open={open} onClose={handleSoftClose} maxWidth="sm" fullWidth>
      <DialogHeader
        title="Lead Targeting for Campaign"
        onClose={handleSoftClose}
      />
      <DialogContent>
        <Column sx={{ gap: 2, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Save stores your criteria in the database. Submit also syncs to
            Airtable and applies filters to your My Leads agent list.
          </Typography>
          {loadError ? (
            <Typography variant="body2" color="error">
              {loadError}
            </Typography>
          ) : null}
          {saveError ? (
            <Typography variant="body2" color="error">
              {saveError}
            </Typography>
          ) : null}
          <Grid container spacing={2}>
            <Grid xs={12}>
              <TextField
                select
                label="Target Type"
                value={form.targetType}
                onChange={(e) => updateField("targetType", e.target.value)}
                fullWidth
                size="small"
                error={!!fieldErrors.targetType}
                helperText={fieldErrors.targetType}
              >
                <MenuItem value="">
                  <em>Select…</em>
                </MenuItem>
                {TARGET_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid xs={12}>
              <TextField
                label="Target Cities"
                value={form.targetCities}
                onChange={(e) => updateField("targetCities", e.target.value)}
                fullWidth
                size="small"
                helperText="Comma-separated"
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Target States"
                value={form.targetStates}
                onChange={(e) => updateField("targetStates", e.target.value)}
                fullWidth
                size="small"
                helperText="Comma-separated"
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Target Counties"
                value={form.targetCounties}
                onChange={(e) => updateField("targetCounties", e.target.value)}
                fullWidth
                size="small"
                helperText="Comma-separated"
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Min radius from office address"
                type="number"
                inputProps={numericInputProps("minRadiusMiles")}
                value={form.minRadiusMiles}
                onChange={(e) => updateField("minRadiusMiles", e.target.value)}
                fullWidth
                size="small"
                error={!!fieldErrors.minRadiusMiles}
                helperText={fieldErrors.minRadiusMiles}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Max radius from office address"
                type="number"
                inputProps={numericInputProps("maxRadiusMiles")}
                value={form.maxRadiusMiles}
                onChange={(e) => updateField("maxRadiusMiles", e.target.value)}
                fullWidth
                size="small"
                error={!!fieldErrors.maxRadiusMiles}
                helperText={fieldErrors.maxRadiusMiles}
              />
            </Grid>
            <Grid xs={12}>
              <Autocomplete
                multiple
                options={INTAKE_BRAND_OPTIONS}
                value={brandCodesToOptions(form.brandsToExclude)}
                onChange={(_, value) =>
                  updateField(
                    "brandsToExclude",
                    value.map((option) => option.value).filter(Boolean)
                  )
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brands to exclude"
                    placeholder=""
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                label="Offices to exclude"
                value={form.officesToExclude}
                onChange={(e) =>
                  updateField("officesToExclude", e.target.value)
                }
                fullWidth
                size="small"
                helperText="Comma-separated office names"
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                label="People to exclude"
                value={form.peopleToExclude}
                onChange={(e) => updateField("peopleToExclude", e.target.value)}
                fullWidth
                size="small"
                helperText="Comma-separated agent names"
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Cities to exclude"
                value={form.citiesToExclude}
                onChange={(e) => updateField("citiesToExclude", e.target.value)}
                fullWidth
                size="small"
                helperText="Comma-separated"
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Counties to exclude"
                value={form.countiesToExclude}
                onChange={(e) =>
                  updateField("countiesToExclude", e.target.value)
                }
                fullWidth
                size="small"
                helperText="Comma-separated"
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Minimum sales (units #)"
                type="number"
                inputProps={numericInputProps("minSalesUnits")}
                value={form.minSalesUnits}
                onChange={(e) => updateField("minSalesUnits", e.target.value)}
                fullWidth
                size="small"
                error={!!fieldErrors.minSalesUnits}
                helperText={fieldErrors.minSalesUnits}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Maximum sales (units #)"
                type="number"
                inputProps={numericInputProps("maxSalesUnits")}
                value={form.maxSalesUnits}
                onChange={(e) => updateField("maxSalesUnits", e.target.value)}
                fullWidth
                size="small"
                error={!!fieldErrors.maxSalesUnits}
                helperText={fieldErrors.maxSalesUnits}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Minimum sales (production $)"
                type="number"
                inputProps={numericInputProps("minSalesProduction")}
                value={form.minSalesProduction}
                onChange={(e) =>
                  updateField("minSalesProduction", e.target.value)
                }
                fullWidth
                size="small"
                error={!!fieldErrors.minSalesProduction}
                helperText={fieldErrors.minSalesProduction}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                label="Maximum sales (production $)"
                type="number"
                inputProps={numericInputProps("maxSalesProduction")}
                value={form.maxSalesProduction}
                onChange={(e) =>
                  updateField("maxSalesProduction", e.target.value)
                }
                fullWidth
                size="small"
                error={!!fieldErrors.maxSalesProduction}
                helperText={fieldErrors.maxSalesProduction}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                label="Additional note"
                value={form.additionalNote}
                onChange={(e) => updateField("additionalNote", e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={3}
              />
            </Grid>
            <Grid xs={12}>
              <BoostCampaignMediaUpload
                additionalMediaUrl={form.additionalMediaUrl}
                criteriaSnapshot={form}
                disabled={isLoading || persistInProgress}
                fieldError={fieldErrors.additionalMediaUrl}
                onBusyChange={setIsMediaBusy}
                onMediaChanged={({ criteria }) => setForm(criteria)}
              />
            </Grid>
          </Grid>
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={handleSoftClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="outlined"
          color="primary"
          loading={isSaving}
          disabled={isLoading || persistInProgress}
          onClick={handleSaveClicked}
        >
          Save
        </LoadingButton>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSubmitting}
          disabled={isLoading || persistInProgress}
          onClick={handleSubmitClicked}
        >
          Submit
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
